import { createLogger } from 'shared/services/logger';
import { triggerUScopeEvent } from '../../_uScope';
import { Point, Size } from '../../lib/objects/';
import { SEARCH_PATTERN, SearchService } from '../../lib/services/search-service';
import { array2DCreate, memset } from '../../lib/utility/helpers';
import us from './../../defines';
import { genericMessage } from './../../toolbar';
import { eRASTERPARMS } from './constants';
import {translate} from 'shared/services/I18nService';
const $t = translate.scope('uScope.extensions.raster-scan');

const logger = createLogger('UScope:extensions:RasterScanService');

// ________________________________________________________________________________________________
// rasterScan.js
//    This file contains the RasterScanService object
//
//    history
//    1410xx     cperz        created
//    _______________________________________end_AIHv1/PR __________________________________________
//    140818    cperz        RasterScanService: manual mode: upon raster panel close reset run events and
//                        set panel state to stopped so manual can restart upon panel open;
//                        remove us.eNavModes.nmRegMove;
//    150318    cperz        add local log,err fns;
//
// ________________________________________________________________________________________________
//    RASTER SCAN OBJECT
// ________________________________________________________________________________________________
//    The raster scan object performs sequencing through an image in an automatic or manual fashion
//    at a user selected magnification and view overlap.  It automatically segements the image
//    and identifies tissue pieces in the image so that the user can sequence the image by tissue
//    piece, by all tissue or just over the whole slide.
//
//
//    Restrictions:
//        - requires HTML5 canvas support
//        - requires a thumbnail from its parent viewport object
//
//    File Dependencies:
//        - imageproc.js for cBlobAnalysis
//        - toolbar.js for cRasterPanel
//
//    Object Dependencies:
//        - created by cViewport
//        - uses objects cBlobAnalysis, a cHeatMap reference
//        - user controls of cRasterPanel
//
//    Design Description:
//    A raster object is created for each viewport. The raster panel, cRasterPanel, created for all of uScope,
//    calls into this raster object to change settings and start, pause, stop the raster sequencing.
//
//        User Specified Settings:
//            All user settings from the cRasterPanel are defined in eRASTERPARMS and controlled
//            by this.parmAction(). Any time a user changes a raster setting raster scan progress is
//            reset and scanning will start at the beginning.
//
//            Automatic Mode: The sequencing is initially started when the user presses the start button
//                on the raster panel. Each view in the image is sequenced without user interaction.
//                Sequencing can be paused with a mouse click on the view or by hitting the space bar.
//                When paused the user can pan and zoom the image at will. Once paused, to continue
//                the user must press the restart button on the raster panel. Sequencing will pick up
//                where it left off.
//            Manual Mode: The sequencing is initially started when the user presses the start button
//                on the raster panel. Each view in the image is sequenced when the user hits the space bar.
//                When paused the user can pan and zoom the image at will.
//            Scan Magnification: The magnification to sequence the image at.
//
//            Heat Map: The outline of the identified tissue is drawn on the thumbnail's and each sequenced
//                view is shaded on the heat map in the color of the scan magnfication.  Heat map drawing is
//                always enabled for raster scan, regardless of the user preference (of disabled).
//
//
//        Code Highlights:
//            load_() creates the 'raster image' by loading the image's thumbnail into a canvas element.
//                maskTissue_() segments the raster image into foreground and background pixels and performs
//                    blob analysis.  It produces the silhouette_ image of blob labels at the raster
//                    image resolution and the blobs_ object, a list of all blobs and their properties.
//                blobDraw_() is called upon to outline each valid blob on the image's thumbnail's heatmap.
//            this.start() is called from the raster panel when the user clicks start/resume
//                or by an event handler when raster scanning is paused and the space bar is hit.
//                If starting raster scan ...
//                    startSearchInit_() determines the search grid step size and dimensions 'csSearchGrid_'
//                        and creates search_, the object that sequences through the grid.
//                    buildSearchMask_() creates the searchMask_, a 2D array with dimensions csSearchGrid_.
//                        Then, in accordance with the user's selection of scan by tissue piece, all tissue
//                        or whole slide it initializes searchMask_'s contents with blob labels.
//                    Event handlers active only during raster scan are bound.
//                If resuming raster scan or starting the raster timer is started which calls on step_().
//            step_() uses the search_ object to select the next valid view grid position in the searchMask_
//                and move the view to that position.  If sequencing by tissue piece, when one blob's area
//                has been scanned it steps to the next blob.  If sequencing has completed, it stops the scan.
//            this.stop() is called from the raster panel when the user stops/pauses the scan
//                or by an event handler when raster scanning is active and the space bar is hit.
//                It halts the raster timer and unbinds the events that are only active during raster scan.
//
//        Coordinate System:
//            cRaster operates with left,top is 0,0; positive x to the right; positive y down
//
// Improvements:
//    - store/restore user prefs
//    - add default vars and assign to vars and use in parmDefault
//    - instead of speedFactor just increase or decrease the timer interval and store/restore timer interval

export default function RasterScanService (uScope, viewport, rasterPanel, blobAnalysis, thumbnail, heatmap, userConfig) {
  var self = this;
  var isLoaded = false; // raster thumb image has been loaded
  var blobDrawTypes = {bbox: false, outline: true}; // blob hightlight options on image thumbnail

  var namespace_ = viewport.eventNameSpaceGet();
  var saveHeatMapState_ = us.ENABLEHEATMAP;

  // ............................................ internal use
  var search_; // search object - sequences through the grid in a specified pattern
  var csSearchGrid_ = new Size(); // granularity of views over image a the search zoom
  var cpStepSizePx_ = new Point(); // relative view move in pixels

  var searchMask_; // view sequencing 2D array, dimensioned as csSearchGrid_ with valid blobs labeled > 0 {1,2,...}
  var maskType_; // one of the us.eSEQUENCESTYLES, type of actually implemented sequence style mask

  var maskValidCount_ = 0; // total number of valid (to be scanned) grid cells in the mask
  var viewCount_ = 0; // running number of valid grid cells scanned
  var progressPct_ = 0;

  var blobs_; // array of found blobs // blobs_[.] = label number, area, bounding box, valid, dimensions, ...  of each blob
  var blobValidCount_ = -1; // number of valid blobs in blobs_
  var blobLabel_ = -1; // currently scanning blob label
  var blobIdx_ = -1; // currently scanning index to blobs_

  var rasterZoom_ = 1.0; // raster zoom at the start of raster scan
  var csStartVPDims_ = new Size(0, 0); // view dimensions at the start of raster scan
  var cpStartGridCoord_ = new Point(0, 0); // initial raster position on search grid, changes depending upon scan pattern and if resuming scan
  var csImageDims_ = new Size(); // the image dimensions at the scan zoom

  var cpCurCoords_ = new Point(0, 0); // view position image position (zoom coords)
  var cpResumeGridCoord_ = new Point(0, 0); // previous raster position on search grid

  var timerId_ = -1; // the rasterScan timer id
  var timerInterval_ = 750; // rasterScan timer interval in milliseconds

  var rasterSpecs = { //
    'thumbScale': 0.25, // scale of raster thumbnail wrt file thumbnail
    'smallBlobArea': 3, // min area threshold on raster thumbnail blob
    'minBlobDim': 3 // minimum width or height of a raster thumbnail blob
  };

  var bSegStatus_ = false; // segmentation status (false means not performed, not valid)
  var pImageCanvas;
  var pImageContext;
  var csRasterImage = new Size(0, 0); // raster image size
  var rasterImageZoom_; // raster image zoom wrt image base level

  var silhouette_; // image of blob labels at the raster image resolution {0:background, >1 blob}
  var backColor_ = 255; // blob analysis binary fore|back ground convention
  var foreColor_ = 0; // note that these must coincide with blob extraction fns

  var bindRunEventsState_ = false; // to insure events are not bound multiple times
  // ............................................ construct_ local
  var construct_ = function () {
    var mag = self.parmAction('default', eRASTERPARMS.MAG); // get the default magnification
    self.parmAction('set', eRASTERPARMS.MAG, mag); // initialize all things dependent upon rasterMag_

    load_();
  };
    // ............................................ user defined parameters
  var rasterMode_ = 'Automatic'; // raster mode {Manual, Automatic}
  var rasterMag_ = 4; // mag[nification]
  var sequenceStyle_ = us.eSEQUENCESTYLES.BYTISSUEPIECES; //

  var speedFactor_ = 100; // speedFactor_ as a percentage of timerInterval_
  var overlapPct_ = 80; // overlap percent

  var patternList = []; // the raster pattern choices
  patternList.push({name: 'Horizontal', num: SEARCH_PATTERN.SNAKE_RIGHT_DOWN});
  patternList.push({name: 'Vertical', num: SEARCH_PATTERN.SNAKE_DOWN_RIGHT});

  var searchPattern_ = SEARCH_PATTERN.SNAKE_RIGHT_DOWN;
  var searchPatternName_ = 'Raster Right';

  var bfThresh_ = 200;
  var flThresh_ = 20;
  var contentPct_ = 15; // todo: a) is this used?  b)maybe this needs to be zoom dependent... bigger area maybe more sensitive? 4x may have a small amout of content

  // ............................................ parmAction() public
  //    parmAction - This function is the interface to set, get, toggle, default user specified settings.
  //        input parameters:
  //            action:        intented action {"set","get","toggle", "default"}
  //            parm:        one of the eRASTERPARMS
  //            val:        value (set action only)
  //
  self.parmAction = function (action, parm, val) {
    var parmSet = function (parm, val) {
      switch (parm) {
        case eRASTERPARMS.MODE:
          rasterMode_ = (val == 'Automatic') ? 'Automatic' : 'Manual';
          self.progressReset('raster mode change'); // reset so paused raster will restart from the beginning
          return rasterMode_;
        case eRASTERPARMS.MAG:
          rasterMag_ = val; // tood: sanity check the value
          rasterZoom_ = viewport.convertToZoom(rasterMag_); // initialize the scan zoom
          csImageDims_ = viewport.getImageDimsAtZoom(rasterZoom_); // initialize the image dims at scan zoom
          self.progressReset('raster magnification change'); // reset so paused raster will restart from the beginning
          // todo: recompute grid?
          return rasterMag_;
        case eRASTERPARMS.OVERLAPPCT:
          if (val < 5) overlapPct_ = 5;
          else if (val > 99) overlapPct_ = 100;
          else overlapPct_ = parseInt(val, 10);
          self.progressReset('raster overlap change'); // reset so paused raster will restart from the beginning
          // todo: recompute grid?
          return overlapPct_;
        case eRASTERPARMS.PATTERNNAME:
          for (var i = 0; i < patternList.length; i++) { // insure a valid pattern number is given
            if (val == patternList[i].name) {
              searchPattern_ = patternList[i].num;
              searchPatternName_ = patternList[i].name;
              break;
            }
          }
          self.progressReset('raster pattern change'); // reset so paused raster will restart from the beginning
          return searchPatternName_; // return the current pattern number
        case eRASTERPARMS.PATTERNNUMBER:
          // nop
          break;
        case eRASTERPARMS.PATTERNLIST:
          // nop
          break;
        case eRASTERPARMS.PIXELTHRESH:
          if (isBrightField == true) bfThresh_ = val;
          else flThresh_ = val;
          self.progressReset('raster threshold change'); // reset so paused raster will restart from the beginning
          return parmGet(eRASTERPARMS.PIXELTHRESH);
        case eRASTERPARMS.CONTENTPCT:
          contentPct_ = val; // todo: sanity check this 3-55 currently from gui
          self.progressReset('raster content pct change'); // reset so paused raster will restart from the beginning
          // todo: recompute grid?
          return contentPct_;
        case eRASTERPARMS.SPEEDFACTOR:
          if (val == 'faster') speedFactor_ = Math.max(speedFactor_ - 10, 10); // faster means smaller interval
          else speedFactor_ = Math.min(speedFactor_ + 10, 400); // slower means larger interval

          if (rasterMode_ == 'Automatic' && timerId_ != -1) { // if autorastering in progress
            clearInterval(timerId_); // stop the rasterScan timer

            var interval = timerInterval_ * (speedFactor_ / 100); // and restart the rasterScan at the new timer interval
            timerId_ = setInterval(step_, interval);
          }
          break;
        case eRASTERPARMS.SEQUENCESTYLE:
          if (val >= us.eSEQUENCESTYLES.BYTISSUEPIECES && val <= us.eSEQUENCESTYLES.WHOLESLIDE) {
            sequenceStyle_ = +val;
            self.progressReset('raster segmentation change to ' + us.strSEQUENCESTYLES[val]); // reset so paused raster will restart from the beginning
          }
          break;
        default:
          break;
      }
    };
    var parmGet = function (parm) {
      switch (parm) {
        case eRASTERPARMS.MODE:
          return rasterMode_;
        case eRASTERPARMS.MAG:
          return rasterMag_;
        case eRASTERPARMS.OVERLAPPCT:
          return overlapPct_;
        case eRASTERPARMS.PATTERNNAME:
          return searchPatternName_;
        case eRASTERPARMS.PATTERNNUMBER:
          return searchPattern_;
        case eRASTERPARMS.PATTERNLIST:
          return patternList; // returns a reference!
        case eRASTERPARMS.PIXELTHRESH:
          return (isBrightField == true) ? bfThresh_ : flThresh_;
        case eRASTERPARMS.CONTENTPCT:
          return contentPct_;
        case eRASTERPARMS.SPEEDFACTOR:
          return speedFactor_;
        case eRASTERPARMS.SEQUENCESTYLE:
          return sequenceStyle_;
        default:
          break;
      }
    };
    var parmDefault = function (parm) {
      switch (parm) {
        case eRASTERPARMS.MODE:
          return parmSet(eRASTERPARMS.MODE, 'Automatic');
        case eRASTERPARMS.MAG:
          return parmSet(eRASTERPARMS.MAG, 4);
        case eRASTERPARMS.OVERLAPPCT:
          return parmSet(eRASTERPARMS.OVERLAPPCT, 80);
        case eRASTERPARMS.PATTERNNAME:
          return parmSet(eRASTERPARMS.PATTERNNAME, patternList[0].name);
        case eRASTERPARMS.PATTERNNUMBER:
          // nop
          break;
        case eRASTERPARMS.PATTERNLIST:
          // nop
          break;
        case eRASTERPARMS.PIXELTHRESH:
          bfThresh_ = 200;
          flThresh_ = 20; // note! defaulting both bf and fl
          self.progressReset('raster threshold change'); // reset so paused raster will restart from the beginning
          return parmGet(eRASTERPARMS.PIXELTHRESH);
        case eRASTERPARMS.CONTENTPCT:
          return parmSet(eRASTERPARMS.CONTENTPCT, 15);
        case eRASTERPARMS.SPEEDFACTOR:
          speedFactor_ = 100;
          return;
        case eRASTERPARMS.SEQUENCESTYLE:
          return parmSet(eRASTERPARMS.BYTISSUEPIECES, us.eSEQUENCESTYLES.BYTISSUEPIECES);
        default:
          break;
      }
    };
    var parmToggle = function (parm) {
      switch (parm) {
        case eRASTERPARMS.MODE:
          return (rasterMode_ == 'Automatic') ? parmSet(eRASTERPARMS.MODE, 'Manual') : parmSet(eRASTERPARMS.MODE, 'Automatic');
        default:
          break;
      }
    };
    // ..................................................
    if (action == 'set') { return parmSet(parm, val); } else if (action == 'get') { return parmGet(parm); } else if (action == 'default') { return parmDefault(parm); } else if (action == 'toggle') { return parmToggle(parm); }

    return undefined;
  };
  // ............................................ progressReset() public
  // progressReset occurs when user prefs change, start, start over
  self.progressReset = function () {
    if (progressGet_() > 0) {
      stop_();
    }

    cpStartGridCoord_.set(0, 0); // raster SNAKE_RIGHT_DOWN and SNAKE_RIGHT_DOWN start position on grid
    viewCount_ = 0;
    blobIdx_ = -1;
    blobLabel_ = -1;

    progressPct_ = 0;
    progressUpdate_();

    heatmap.erase();

    bSegStatus_ = maskTissue_(); // segment the image at resterImage resolution (foreground / background);
    blobDraw_(); // draw the segmentation results on the thumbnail if the raster panel is open
  };
  // ............................................ progressUpdate_() local
  var progressUpdate_ = function () {
    thumbnail.viewFinderMove('raster'); // update the thumbnail viewfinder right now

    progressPct_ = progressGet_();
    triggerUScopeEvent(us.evRASTERPROGRESS, {progressPct: progressPct_}); // fire event to broadcast rasterScan progress
  };
    // ............................................ progressGet_() local
  var progressGet_ = function () {
    progressPct_ = (maskValidCount_ > 0) ? progressPct_ = parseInt(100 * viewCount_ / maskValidCount_, 10) : 0; //
    return progressPct_;
  };

    // ............................................ // misc
  var isBrightField = (viewport.getCaptureType() != us.eAICCaptureType.aic_i_FL);

  // ............................................ onPanelOpen() public
  // This function is called when the raster panel is opened
  self.onPanelOpen = function () {
    saveHeatMapState_ = userConfig.get(us.CVSHEATMAP);
    if (saveHeatMapState_ == false) { userConfig.set(us.CVSHEATMAP, true); }

    blobDraw_(); // draw the segmentation results on the thumbnail if the raster panel is open
  };
  // ............................................ resetHeatMapState() public
  // This function is called when the raster panel is closed and when the raster scan mode is halted
  // to insure that the heat map visibility is returned to its original user preference prior to raster scanning.
  self.resetHeatMapState = function () {
    // if the raster panel is closed AND no longer raster scanning hide the heatmap if the original user pref was so
    if (rasterPanel.isOpen() == false && viewport.navModeGet() == us.eNavModes.nmNone) {
      if (saveHeatMapState_ == false) { userConfig.set(us.CVSHEATMAP, false); }
    }
  };
  // ............................................ bindEvents() local
  var bindEvents = function () {
    $(us.idHashROOT).on(us.evIMAGESELECTED + namespace_, function (e, parms) { // image select event
      if (viewport.getImageIdx() == uScope.getSelImageIdx()) { // .....if this image is the selected image
        if (rasterPanel.isOpen() == true) { // if the rasterscan panel is open
          rasterPanel.position(); // then reposition the panel onto this image
          rasterPanel.pause(); // pause raster scan
        }
      } else { // ..... else this image is NOT the selected image
        if (viewport.navModeGet() == us.eNavModes.nmRasterScan) { // if in rasterScan mode,
          rasterPanel.pause(); // pause raster scan
        }
      }
    });

    viewport.events.on('viewport:resize', self.progressReset);

    // Stop raster scan when readOnly attribute for a plugin (VP) is true.
    viewport.events.on('state-readonly:change', (isReadOnly) => {
      if (isReadOnly) {
        self.stop();
      }
    });

    // insure heatmap state is reset when halting raster scan
    viewport.events.on('viewport:stop-navigation', (navMode) => {
      if (navMode === us.eNavModes.nmRasterScan) {
        self.resetHeatMapState();
        return true;
      }
    });
  };

    // ............................................ bindRunEventsOn_() local
    // This function binds the space bar event to pause/resume scanning.
    //    The event is unbound when the raster panel is closed or the scan fully completes
  var bindRunEventsOn_ = function () {
    if (bindRunEventsState_ == true) return;

    $(us.idHashROOT).on(us.evSPACEBAR + namespace_, function (e, parms) {
      if (rasterMode_ == 'Automatic') {
        if (timerId_ != -1) {
          stop_('spacebar stop'); // if rasterScan is running, stop
        } else {
          start_(); // if rasterScan is not running, start
        }
      } else {
        step_();
      }
      return false;
    });
    bindRunEventsState_ = true;
  };
    // ............................................ bindRunEventsOff_() local
  var bindRunEventsOff_ = function () {
    $(us.idHashROOT).off(us.evSPACEBAR + namespace_);
    bindRunEventsState_ = false;
  };
    // ............................................ start_ local
  var start_ = function () {
    rasterPanel.start();
  };
    // ............................................ start() public
    //    This function starts/resumes rasterScan scanning.
  self.start = function () {
    if (viewport.navModeGet() === us.eNavModes.nmRasterScan) {
      return;
    }

    viewport.navModeSwitch(us.eNavModes.nmRasterScan); // zooming changed nav mode to none, so change it back

    var bStart = (progressPct_ == 0 || progressPct_ > 98); // are we starting at the beginning or resuming?

    // if the view has been resized since the last rasterScan start or it has been zoomed and we are not told to resume the raster,
    // reset the start of raster position
    var csVPDivDimsRightNow = viewport.vpDivDimsGet(); // if a resize or mag change has occurred, stop rasterScan
    if (csVPDivDimsRightNow.w != csStartVPDims_.w || csVPDivDimsRightNow.h != csStartVPDims_.h) {
      bStart = true;
      if (progressPct_ > 0) {
        logger.warn('view resize change occured: must restart raster from the beginning.');
      }
    }

    if (bStart == true) { // start new grid......................
      self.progressReset('start');
      heatmapStyle(); // set the thumbnail heat map style for raster scanning
      if (startSearchInit_(csVPDivDimsRightNow) == false) { // compute the step size and grid dimensions 'csSearchGrid_'
        stop_('Search initialization failed'); // on error, auto halt
        return;
      }

      buildSearchMask_(); // produce the searchMask_ of dimensions 'csSearchGrid_' and update blobs_ array
      if (blobValidCount_ < 1) {
        stop_('Blob mask creation failed'); // if there's no blobs, don't start
        return;
      }

      blobLabel_ = blobNextValid_(); // perform raster sequence one blob at a time, get first valid blob
      if (blobLabel_ < 0) {
        stop_('Blob label invalid'); // if there's no blobs, don't start
        return;
      }

      bindRunEventsOn_(); // active raster run events
    } else { // resume on current grid .............
      heatmapStyle(); // set the thumbnail heat map style for raster scanning
      bindRunEventsOn_(); // active raster run events

      // resume begins at the prior view position
    }

    if (rasterZoom_ != viewport.getViewZoom()) { // set the view zoom to the original raster scan zoom
      viewport.zoomAbsolute(rasterZoom_);
    }

    viewport.navModeSwitch(us.eNavModes.nmRasterScan); // zooming changed nav mode to none, so change it back
    viewport.focus(); // set focus to this viewport so spacebar event can be captured

    if (rasterMode_ == 'Automatic') {
      var interval = timerInterval_ * (speedFactor_ / 100);
      timerId_ = setInterval(step_, interval); // and start the rasterScan sequence
    }
    // ..................................................
    // closure functions
    // This function sets the heatmap style for raster scan.
    function heatmapStyle () {
      if (heatmap) {
        var alpha = Math.min(0.3, overlapPct_ / 100);
        heatmap.alphaSet(alpha);
        var color = us.objectiveColor(rasterMag_);
        heatmap.fillColorSet(color);
        heatmap.strokeColorSet(color);
      }
    }
  };

  // ............................................ stop_() local
  var stop_ = function () {
    // internal stop to simulate user click pause button
    rasterPanel.pause(); // pause raster scan
  };
    // ............................................ stop() public
    //    This function turns off/pauses rasterScan.
    //    Note that the spacebar event is not unbound until the raster panel is closed........

    // toconsider: pause verse stop where stop would unbind the spacebar
    // toconsider: could report state of scan and determine if halted mid-scan

  self.stop = function () {
    viewport.navModeHalt(); // halt raster scan navigation mode

    clearInterval(timerId_); // stop the rasterScan timer
    timerId_ = -1; // and reset its value

    progressUpdate_(); // insure the user sees the current progress

    // if manual scan or if automatic scan is complete, remove the raster run events
    if (rasterMode_ == 'Manual' || (rasterMode_ == 'Automatic' && progressPct_ > 99)) { bindRunEventsOff_(); }

    if (thumbnail.heatMap) { thumbnail.heatMap.contextDefault(); } // reset heatmap context so any user navigation is drawn normally
  };
  // ............................................ step_() local
  //    This function moves the view one grid position
  var step_ = function () {
    // ....................... stop if if no longer in raster mode
    if (viewport.navModeGet() != us.eNavModes.nmRasterScan) {
      logger.warn('step: navMode:' + navModeGetStr() + ' expecting raster mode, stopping');
      stop_(' navMode:' + navModeGetStr() + ' expecting raster mode, stopping');
      return;
    }

    // This used to wait till all tiles in the viewport loaded (or a preset timeout was hit) before moving on.
    // TODO: Restore if necessary.

    // ....................... stop if a resize or mag change has occurred
    var iterator;
    var csVPDivDimsRightNow = viewport.vpDivDimsGet();
    if (csVPDivDimsRightNow.w != csStartVPDims_.w || csVPDivDimsRightNow.h != csStartVPDims_.h || viewport.getViewZoom() != rasterZoom_) {
      stop_('view resized or zoomed'); // auto halt
      return;
    }

    // .......................start or resume rastering
    do { // loop until a valid mask cell is found but halt rasterScan if at the end of the search
      iterator = search_.next(); // get the next grid coords in the sequence
      if (!iterator) { // 1 check: is this next grid position valid to scan?
        blobLabel_ = blobNextValid_(); // 1 no: then move on to the next blob to scan

        if (blobLabel_ >= 0) { // 2 check: is the new blob label valid? if so, restart scan for next blob
          cpStartGridCoord_.set(0, 0); // 2 yes:  the new blob label is valid
          // so, create the search object that will sequence the raster for the next blob
          search_ = new SearchService(cpStartGridCoord_, csSearchGrid_, searchPattern_);
          iterator = search_.next(); // and get started
        } else { // 2 no: the new blob label is not valid
          self.stop();
          stop_('scan finished'); // change  then auto halt when scan is complete
          return;
        }
      } // check that the mask indicates a valid scan position, if not then loop
    } while (searchMask_[iterator.x][iterator.y] != blobLabel_);

    cpCurCoords_.x = iterator.x * cpStepSizePx_.x * rasterZoom_; // compute the view position in zoom coords
    cpCurCoords_.y = iterator.y * cpStepSizePx_.y * rasterZoom_;

    viewport.moveAbsolute(cpCurCoords_.x, cpCurCoords_.y, {animate: true});

    cpResumeGridCoord_.set(iterator.x, iterator.y); // save the last grid position (for resume)

    progressUpdate_();
    viewCount_++; // update the number of grid cells scanned
  };
    // ............................................ buildSearchMask_() public
    //    buildSearchMask_ -This function builds the scan mask 'searchMask_' which is a grid of views labeled
    //    with blob numbers used to sequence the raster scan through the image.  It also updates the blobs_
    //    array and blobValidCount_ which define the labeled tissue pieces.
    //
    //    It will first try to initialize the mask for the sequence style the user selected
    //    but if that does not succeed it will gracefully degrade to a sequence style it can peform.
    //

  var buildSearchMask_ = function () {
    maskType_ = sequenceStyle_; // maskType_ is the actually implmented sequence style

    // cp: can this be inside of maskTissueToGrid_ and maskAllTissueToGrid_

    if (!bSegStatus_) { // if tissue mask was not created on load_()
      maskType_ = us.eSEQUENCESTYLES.WHOLESLIDE; // then force a whole slide scan
    }

    // gracefully degrade ....
    if (maskType_ == us.eSEQUENCESTYLES.BYTISSUEPIECES) { // if desire is to raster scan each piece of tissue one at a time
      if (!maskTissueToGrid_()) { // map the high resolution tissue silhouette to the grid in searchMask_
        maskType_ = us.eSEQUENCESTYLES.WHOLESLIDE; // if failed, step down to whole slide
      }
    }
    if (maskType_ == us.eSEQUENCESTYLES.WHOLESLIDE) { // if desire or fallback is to raster scan the whole slide
      maskEverythingToGrid_();
    }

    // This can be undefined if the user changes mask type before starting a scan.
    // TODO: This was added as a bugfix; it's logic wasn't validated. Additional work may need to be done.
    if (!searchMask_) {
      searchMask_ = array2DCreate(csSearchGrid_.w, csSearchGrid_.h);
    }

    return maskType_;
  };
    // ............................................ startSearchInit_() local
    //    startSearchInit_ - This function initializes the search by setting up the search grid step size, dimensions, etc.
    //    Returns true if successful, false otherwise.
  var startSearchInit_ = function (csVPDivDimsRightNow) {
    csStartVPDims_.set(csVPDivDimsRightNow.w, csVPDivDimsRightNow.h); // initialize the vp div dims at start of raster

    if (searchPattern_ == SEARCH_PATTERN.SNAKE_RIGHT_DOWN) { // raster right_down has more overlap in x than in y
      cpStepSizePx_.set(parseInt((100.0 - overlapPct_) / 100 * csVPDivDimsRightNow.w, 10), parseInt(overlapPct_ / 100 * csVPDivDimsRightNow.h, 10));
    } else if (searchPattern_ == SEARCH_PATTERN.SNAKE_DOWN_RIGHT) {
      cpStepSizePx_.set(parseInt(overlapPct_ / 100 * csVPDivDimsRightNow.w, 10), parseInt((100.0 - overlapPct_) / 100 * csVPDivDimsRightNow.h, 10));
    } else {
      logger.warn('unknown search pattern');
      genericMessage($t('search-pattern-not-recognized'), 'AutoRaster');
      return false;
    }

    // now determine the search grid dimensions
    //    The search grid dimensions is defined by the number of views of size cpStepSizePx_ necessary to cover the image at the given scan zoom.
    var x = 0;
    var xStep = cpStepSizePx_.x; // compute the number of views in the X-direction
    while (((x * xStep) + csStartVPDims_.w) <= csImageDims_.w) {
      x++;
    }
    csSearchGrid_.w = x + 1;

    var y = 0;
    var yStep = cpStepSizePx_.y; // compute the number of views in the Y-direction
    while ((y * yStep + csStartVPDims_.h) <= csImageDims_.h) {
      y++;
    }
    csSearchGrid_.h = y + 1;

    if (csSearchGrid_.w < 2 && csSearchGrid_.h < 2) { // sanity check
      logger.warn('startSearchInit_: search grid is too small: ' + csSearchGrid_.w + 'x' + csSearchGrid_.h);
      genericMessage($t('search-area-raster-magnification-small', {mag: rasterMag_}), 'AutoRaster');
      return false;
    }

    // create the search object that will sequence the raster
    search_ = new SearchService(cpStartGridCoord_, csSearchGrid_, searchPattern_);

    return true;
  };
    // ............................................ maskTissueToGrid_() local
    //    This function maps the high resolution silhouette_ 2d array of blob labels to the view-step (aka grid)
    //    resolution of searchMask_. It creates the searchMask_ grid with each tissue piece identified as a unique blob.
    //    It also initializes the blobs_ array and blobValidCount_.
    //    It returns true if successful and false otherwise.

  var maskTissueToGrid_ = function () {
    if (silhouette_ == undefined) {
      logger.error('maskTissueToGrid_: requires the mask to be prepared');
      return false;
    }

    maskValidCount_ = 0;

    searchMask_ = array2DCreate(csSearchGrid_.w, csSearchGrid_.h); // create the blob mask array at the grid dimensions
    array2DSet(searchMask_, searchMask_.width, searchMask_.height, 0); // clear it to zero

    var csImgDims = viewport.getImageDims();
    var gridScaleX = csImgDims.w / searchMask_.width; // the scale between image base coords and the searchMask_
    var gridScaleY = csImgDims.h / searchMask_.height;

    var xbase = 0;
    var ybase = 0; // the image base coord of a silhouette cell
    var baseStep = 1.0 / rasterImageZoom_; //
    for (var y = 0, ylen = silhouette_.height; y < ylen; y++) { // step
      xbase = 0;
      var gridY = parseInt(ybase / gridScaleY, 10);

      for (var x = 0, xlen = silhouette_.width; x < xlen; x++) {
        var gridX = parseInt(xbase / gridScaleX, 10); // cp-can probably incr this too....
        if (silhouette_[x][y] > 0) {
          if (gridX < searchMask_.width && gridY < searchMask_.height) {
            if (searchMask_[gridX][gridY] == 0) { // increment the number of to-be-scaned grid cells
              maskValidCount_++;
            }
            searchMask_[gridX][gridY] = silhouette_[x][y]; // silhouette_'s blob number moved to searchMask_
          }
        }
        xbase += baseStep;
      }
      ybase += baseStep;
    }
    return true;
  };

    // ............................................ maskEverythingToGrid_() local
    // This function creates the searchMask_ grid with all grid cells identified as one blob (the whole slide).
    //    It also initializes the blobs_ array (of one foreground blob) and blobValidCount_ (to 1).
    //    It returns true if successful and false otherwise.

  var maskEverythingToGrid_ = function () {
    maskValidCount_ = csSearchGrid_.w * csSearchGrid_.h;

    searchMask_ = array2DCreate(csSearchGrid_.w, csSearchGrid_.h); // create the 2D mask array
    array2DSet(searchMask_, searchMask_.width, searchMask_.height, 1); // assign entire grid to blob label 1

    var blobIn = new Array(maskValidCount_); // 1D array input to blob detection
    memset(blobIn, 0, maskValidCount_, foreColor_); // set the array to foreground value
    var labelMask1D = blobAnalysis.detect(blobIn, searchMask_.width, searchMask_.height);
    blobs_ = blobAnalysis.getBlobs(labelMask1D, searchMask_.width, searchMask_.height); // blobs_[.] = label number, area and bounding boxes of each blob

    blobIntegrity_(labelMask1D); // adds blob dimensions and valid properties, combines intersecting blobs, sets valid state
    return true;
  };
    // ............................................ maskTissue_() local
    //    maskTissue_ - This function performs blob detection on the high resolution thumbnail
    //    producing the blobs_ array listing the blobs found and their attributes and the
    //    silhouette_ 2D image of the tissue labeled by blob number.
    //
    // improvement option: add a 1 pixel border around the image for blob extraction's edge issues

  var maskTissue_ = function () {
    blobValidCount_ = 0;

    if (!isLoaded) {
      return false;
    }

    // threshold the raster image in the canvas and save the results in a 1D array in preparation for blob analysis
    var i, b, len;
    var imgData = pImageContext.getImageData(0, 0, csRasterImage.w, csRasterImage.h); // get image data from canvas
    var blobIn = new Array(csRasterImage.w * csRasterImage.h); // create a 1D array for blob extraction to use
    if (isBrightField) { // is BF, green channel > threshold?
      for (i = 1, b = 0, len = csRasterImage.w * csRasterImage.h * 4; i < len; i += 4, b++) { // start i at 1 addresses the green channel
        if (imgData.data[i] < bfThresh_) {
          blobIn[b] = foreColor_;
        } else {
          blobIn[b] = backColor_;
        }
      }
    } else { // is FL, sum of channels > threshold?
      for (i = 0, b = 0, len = csRasterImage.w * csRasterImage.h * 4; i < len; i++, b++) {
        var sum = imgData.data[i++] + imgData.data[i++] + imgData.data[i++];
        if (sum > flThresh_) {
          blobIn[b] = foreColor_;
        } else {
          blobIn[b] = backColor_;
        }
      }
    }

    // perform blob detection: input is blobIn, output is labelMask1D (a 1D array of blob labels)
    var labelMask1D = blobAnalysis.detect(blobIn, csRasterImage.w, csRasterImage.h);
    labelMask1D.length = csRasterImage.w * csRasterImage.h;

    // get the blob list from the labelMask1D into the blobs_ object
    // blobs_[.] = label number, area and bounding boxes of each blob
    blobs_ = blobAnalysis.getBlobs(labelMask1D, csRasterImage.w, csRasterImage.h);

    // filter the blob list to remove small blobs and combine intersecting blobs, order it, add a valid property
    blobIntegrity_(labelMask1D);

    // convert the labelMask1D to the silhouette_ 2D array of labels {0:background, >1 blob}  which is at the resolution of the raster image
    silhouette_ = array2DCreate(csRasterImage.w, csRasterImage.h); // silhouette_ is a 2D mask at raster thumb resolution containing blob labels
    for (var y = 0, s = 0, ylen = silhouette_.height; y < ylen; y++) { // fill the silhouette_ with the tissue label
      for (var x = 0, xlen = silhouette_.width; x < xlen; x++, s++) {
        if (labelMask1D[s] > 0) silhouette_[x][y] = labelMask1D[s];
        else silhouette_[x][y] = 0;
      }
    }
    return true;
  };
    // ............................................ blobIntegrity_() local
    //    blobIntegrity_ - This function adds blob dimensions and valid properties,
    //    combines intersecting blobs, sets valid state, and counts the number of valid blobs.
    //    It  modifies the labels and valid flag in the blobs_ array and produces blobValidCount_.
  var blobIntegrity_ = function (labelMask1D) {
    blobValidCount_ = 0;
    var b;
    var bcnt = blobs_.length;
    if (bcnt > 1) { // there must be more than 1 blob (1st blob is whole image)
      blobs_[0].valid = false; // blobs_[0] is alsways the whole slide so just invalidate it for raster purposes

      var swapped; // sort the blobs in ascending X order - this then defines raster scan order
      do {
        swapped = false;
        for (b = 0; b < bcnt - 1; b++) {
          if (blobs_[b].x1 > blobs_[b + 1].x1) {
            var temp = blobs_[b];
            blobs_[b] = blobs_[b + 1];
            blobs_[b + 1] = temp;
            swapped = true;
          }
        }
      } while (swapped);

      for (var t = 0; t < bcnt; t++) { // if any blobs are fully inside another, join them
        for (b = 0; b < bcnt; b++) {
          if (t != b && blobs_[t].valid == true && blobs_[b].valid == true) {
            if (blobBoxInside_(blobs_[t], blobs_[b])) {
              if (blobs_[t].area > blobs_[b].area) {
                replaceLabels(blobs_[b].l, blobs_[t].l); // replace all b labels with b-1
                blobs_[b].valid = false;
              } else {
                replaceLabels(blobs_[t].l, blobs_[b].l); // replace all b-1 labels with b
                blobs_[t].valid = false;
              }
            }
          }
        }
      }
      // var strValid = '';
      var smallBlobArea = rasterSpecs.smallBlobArea;
      var minDim = rasterSpecs.minBlobDim;
      if (smallBlobArea > 0) {
        for (b = 0; b < bcnt; b++) { // remove really small blobs, long thin blobs (coverslip edges)
          if (blobs_[b].valid == true) {
            var blob = blobs_[b];
            if (blob.area > smallBlobArea && blob.width > minDim && blob.height > minDim) {
              // strValid += '\n #' + b + ' label: ' + blob.l + ' area: ' + blob.area + ',' + ' width: ' + blob.width + ',' + ' height: ' + blob.height;
            } else {
              blob.valid = false; // invalidate small blobs
              replaceLabels(blob.l, 0); // replace all of its labels with 0, indicating background
            }
          }
        }
      }

      blobAnalysis.addBlobEdges(blobs_, labelMask1D); // add blob edge array to valid blobs

      for (b = 0; b < bcnt; b++) { // count the number of valid blobs left
        if (blobs_[b].valid == true) blobValidCount_++;
      }
    }
    return blobValidCount_;

    // ..................................................
    // closure functions
    // improvements:
    //    - move to imageproc.js?
    function replaceLabels (origLabel, newLabel) { // This function replaces a label in the labelMask1D array
      for (var i = 0, len = labelMask1D.length; i < len; i++) { if (labelMask1D[i] == origLabel) labelMask1D[i] = newLabel; }
    }

    function blobBoxInside_ (blobA, blobB) { // This function answers the question: is blobB inside of blobA?
      return (blobB.x1 > blobA.x1 &&
                    blobB.x2 < blobA.x2 &&
                    blobB.y1 > blobA.y1 &&
                    blobB.y2 < blobA.y2);
    }
  };
    // ............................................ blobDraw_() local
    // blobDraw_ - This function draws the bounding box of each valid blob found in the silhouette_ image on the heatmap.
  var blobDraw_ = function () {
    var blobDrawBBox_ = function () {
      for (var b = 0, bcnt = blobs_.length; b < bcnt; b++) {
        if (blobs_[b].valid == false) continue;
        var blob = blobs_[b]; // blobs are in raster image dims

        var x1 = parseInt(blob.x1 * scale, 10);
        var y1 = parseInt(blob.y1 * scale, 10);
        var w = (blob.x2 - blob.x1 + 1) * scale;
        var h = (blob.y2 - blob.y1 + 1) * scale;
        heatmap.drawRect(x1, y1, w, h, 1.0, outlineColor);
      }
    };
    // This function draws the outline of each valid blob found in the silhouette_ image on the heatmap.
    var blobDrawEdges_ = function () {
      var edgesInGrid = []; // to hold edges in thumbnail overlay dims
      for (var b = 0, bcnt = blobs_.length; b < bcnt; b++) {
        if (blobs_[b].valid == false) continue;
        var edges = blobs_[b].edges; // blobs edges are in raster image dims
        for (var e = 0, len = edges.length; e < len; e++) {
          var x1 = parseInt(edges[e].x * scale, 10); // convert the pixel position to thumbnail coords
          var y1 = parseInt(edges[e].y * scale, 10);
          edgesInGrid.push({x: x1, y: y1});
        }
      }
      heatmap.drawPixels(edgesInGrid, outlineColor, true); //
    };

    if (!rasterPanel || !rasterPanel.isOpen()) { // don't draw unless the user has the raster panel open
      return;
    }

    if (blobs_ == undefined || silhouette_ == undefined || heatmap == undefined || blobValidCount_ == 0) { return; }

    var outlineColor = us.objectiveColor(rasterMag_);

    var csThumb = thumbnail.dimensionsGet();
    var scale = csThumb.w / csRasterImage.w;

    if (blobDrawTypes.bbox == true) { // draws the bounding box of each valid blob found in the silhouette_ image on the heatmap
      blobDrawBBox_();
    }
    if (blobDrawTypes.outline == true) { // draws the outline of each valid blob found in the silhouette_ image on the heatmap
      blobDrawEdges_();
    }
  };
    // ............................................ blobNextValid_() local
    // This function returns the next valid blob label in the blobs_ array or -1 if none are valid.
    // It maintains the local blobIdx_ and blobLabel_ vars.
  var blobNextValid_ = function () {
    for (var b = blobIdx_ + 1; b < blobs_.length; b++) {
      if (blobs_[b].valid == true) {
        blobIdx_ = b;
        blobLabel_ = blobs_[b].l;
        return blobLabel_;
      }
    }
    blobIdx_ = -1;
    blobLabel_ = -1;
    return blobLabel_;
  };

    // ............................................ load_() local
    //    This function loads into a canvas element a thumbnail image that will be used for segmentation.
    //
    //    toconsider:
    //        - the id approach here assumes the same image is not opened twice - fix that with a random value
  var load_ = function () {
    if (uScope.isThisImageIdValid(viewport.getImageId()) == false) { // protect against rapid slide close/open sequence
      return logger.error('load_: image is not loaded');
    }

    var vpclassid = us.classFromImageId(viewport.getImageId()); // convert the imageId into a class name
    var rasterImageTagId = us.IMGRASTER + '_' + vpclassid;

    // create the progressive image canvas tag where the raster image will reside (whole image but small like a thumbnail)
    pImageCanvas = document.createElement('canvas');
    pImageContext = pImageCanvas.getContext('2d');

    var csThumbDims = viewport.getThumbFileDims();
    var width = Math.floor(csThumbDims.w * rasterSpecs.thumbScale); // the requested thumbnail width
    var sRasterImgSrc = viewport.urlBuilder.thumb(width, 0);
    var csImgDims = viewport.getImageDims(); // compute progressive image width, height and zoom

    rasterImageZoom_ = width / csImgDims.w;

    var imageLoader = new Image();
    imageLoader.onload = function () {
      isLoaded = true;
      csRasterImage.set(imageLoader.width, imageLoader.height);

      pImageCanvas.width = csRasterImage.w; // size the canvas
      pImageCanvas.height = csRasterImage.h;

      pImageContext.drawImage(imageLoader, 0, 0, csRasterImage.w, csRasterImage.h);
      bSegStatus_ = maskTissue_(); // segment the image at rasterImage resolution (foreground / background);
      blobDraw_(); // draw the segmentation results on the thumbnail if the raster panel is open
      bindEvents();

      pImageCanvas.appendChild(imageLoader);
      // on IE11 chokes (something to do with CORS; not honestly sure what...)
    };
    imageLoader.onerror = function (e) {
      isLoaded = false;
      logger.error('RasterScanService - failed to load image', e);
    };
    // Allow direct imageserver accessed raster image to use canvas.getImageData without being tainted by cross-origin rules.
    imageLoader.crossOrigin = 'anonymous';
    imageLoader.id = rasterImageTagId;
    imageLoader.src = sRasterImgSrc;
  };

  const navModeKeys = Object.keys(us.eNavModes);
  function navModeGetStr () {
    return navModeKeys[viewport.navMode];
  }

  construct_();
}

function array2DSet (anArray, width, height, value) {
  for (var x = 0; x < width; x++) {
    for (var y = 0; y < height; y++) {
      anArray[x][y] = value;
    }
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/raster-scan/raster-scan-service.js